import { API } from "revolt.js";
import styles from "./Attachment.module.scss";
import classNames from "classnames";
import { useTriggerEvents } from "preact-context-menu";
import { useState } from "preact/hooks";

import { gifPlayable } from "../../../../../packages/revolt.js/src/preview";
import { useClient } from "../../../../controllers/client/ClientController";
import AttachmentActions from "./AttachmentActions";
import GifFile from "./GifFile";
import { SizedGrid } from "./Grid";
import ImageFile from "./ImageFile";
import Spoiler from "./Spoiler";
import TextFile from "./TextFile";

interface Props {
    attachment: API.File;
    hasContent?: boolean;
    allAttachments?: (API.File | API.Embed)[];
}

const MAX_ATTACHMENT_WIDTH = 480;

export default function Attachment({
    attachment,
    hasContent,
    allAttachments,
}: Props) {
    const client = useClient();
    const { filename, metadata } = attachment;
    const [spoiler, setSpoiler] = useState(filename.startsWith("SPOILER_"));

    const url = client.generateFileURL(
        attachment,
        { width: MAX_ATTACHMENT_WIDTH * 1.5 },
        true,
    );

    switch (metadata.type) {
        case "Image": {
            return (
                <SizedGrid
                    data-attachment
                    width={metadata.width}
                    height={metadata.height}
                    {...useTriggerEvents("Menu", {
                        attachment,
                    })}
                    className={classNames({
                        [styles.margin]: hasContent,
                        spoiler,
                    })}>
                    {attachment.content_type === "image/gif" &&
                    !gifPlayable(attachment) ? (
                        <GifFile
                            allAttachments={allAttachments}
                            attachment={attachment}
                            width={metadata.width}
                            height={metadata.height}
                        />
                    ) : (
                        <ImageFile
                            allAttachments={allAttachments}
                            attachment={attachment}
                            width={metadata.width}
                            height={metadata.height}
                        />
                    )}
                    {spoiler && <Spoiler set={setSpoiler} />}
                </SizedGrid>
            );
        }

        case "Video": {
            return (
                <div
                    data-attachment
                    className={classNames(styles.container, {
                        [styles.margin]: hasContent,
                    })}
                    style={{ "--width": `${metadata.width}px` }}
                    {...useTriggerEvents("Menu", {
                        attachment,
                    })}>
                    <AttachmentActions attachment={attachment} />
                    <SizedGrid
                        width={metadata.width}
                        height={metadata.height}
                        className={classNames({ spoiler })}>
                        <video
                            src={url}
                            alt={filename}
                            poster={client.generatePreviewFileURL(attachment)}
                            controls
                            loading="lazy"
                            width={metadata.width}
                            height={metadata.height}
                            onMouseDown={(ev) =>
                                ev.button === 1 && window.open(url, "_blank")
                            }
                        />
                        {spoiler && <Spoiler set={setSpoiler} />}
                    </SizedGrid>
                </div>
            );
        }

        case "Audio": {
            return (
                <div
                    data-attachment
                    className={classNames(styles.attachment, styles.audio)}
                    data-has-content={hasContent}
                    {...useTriggerEvents("Menu", {
                        attachment,
                    })}>
                    <AttachmentActions attachment={attachment} />
                    <audio src={url} controls />
                </div>
            );
        }

        case "Text": {
            return (
                <div
                    data-attachment
                    className={classNames(styles.attachment, styles.text)}
                    data-has-content={hasContent}
                    {...useTriggerEvents("Menu", {
                        attachment,
                    })}>
                    <TextFile attachment={attachment} />
                    <AttachmentActions attachment={attachment} />
                </div>
            );
        }

        default: {
            return (
                <div
                    data-attachment
                    className={classNames(styles.attachment, styles.file)}
                    data-has-content={hasContent}
                    {...useTriggerEvents("Menu", {
                        attachment,
                    })}>
                    <AttachmentActions attachment={attachment} />
                </div>
            );
        }
    }
}
